/**
 * IMPORTS
 */
import removeDiacritics from 'src/aggregates/utils/diacritics';
import highlight from 'src/aggregates/utils/highlighter';
import Store from 'src/store';


/**
 * TYPES
 */
import {ISearch} from 'src/aggregates/customers/selectors.d';
import {IContact} from 'src/aggregates/customers/state.d';
import {ICustomer} from 'src/aggregates/customers/state.d';
import {ICustomerById} from 'src/aggregates/customers/state.d';
import {ICustomerState} from 'src/aggregates/customers/state.d';


/**
 * CODE
 */

/**
 * I fetch a customer.
 *
 * :param id: customer id
 *
 * :returns: customer
 */
function fetch (id: number | string): ICustomer
{
    return Store.getState().customer.byId.get(id);
}


/**
 * I get a contact by its channel type id and uid.
 *
 * :param channel: contact channel type id
 * :param uid: contact uid
 *
 * :returns: contact, nothing if contact is not found
 */
function getContactByChannelAndUid (
    channel: number,
    uid: string,
): IContact | null
{
    // get the customer holding the contact
    const customer = getCustomerByContact(channel, uid);

    // customer not found: return null
    if (customer === null)
    {
        return null;
    }

    // return the contact from the customer contacts
    return customer.contacts.find(
        contact => contact.type_id === channel && contact.value == uid,
    ) ?? null;
}


/**
 * I get a contact by its id.
 *
 * :param state: customer state
 * :param id: contact id
 *
 * :returns: contact, nothing if id is not found
 */
function getContactById (
    id: number | string,
): IContact | null
{
    // search for contact in each customer
    for (const customer of getState().byId.values())
    {
        // search for contact in each customer contact
        for (const contact of customer.contacts)
        {
            // contact found: return it
            if (contact.id === id)
            {
                return contact;
            }
        }
    }

    // contact not found: return null
    return null;
}


/**
 * I get a contact in the customer.
 *
 * :param channel: contact channel type id
 * :param customer: customer id to search in
 * :param uid: contact uid
 *
 * :returns: contact or nothing
 */
function getContactInCustomer (
    channel: number,
    customer: number | string,
    uid: string,
): IContact | null
{
    // get customer by id
    const fetchedCustomer = fetch(customer);

    // customer not found: return null
    if (fetchedCustomer === undefined)
    {
        return null;
    }

    // get contact in customer
    const contact = fetchedCustomer.contacts.find(
        contact => contact.value === uid && contact.type_id === channel,
    );

    // return contact or null
    return contact ?? null;
}


/**
 * I get the current customer's id.
 *
 * :returns: current customer id or null
 */
function getCurrentCustomer (): string | number | null
{
    return Store.getState().customer.currentCustomer;
}


/**
 * I get a customer by contact.
 *
 * :param channel: contact channel type id
 * :param uid: contact uid
 *
 * :returns: first customer found, nothing otherwise
 */
function getCustomerByContact (channel: number, uid: string): ICustomer | null
{
    // get customer state
    const state = getState();

    // find a customer which contains passed contact
    const id = state.byContact.get(`${channel}_${uid}`);

    // get customer by id
    const customer = state.byId.get(id);

    // return customer if exist or null
    return customer ?? null;
}


/**
 * I get the customer name by id
 *
 * :param id: customer id
 *
 * :returns: customer name
 */
function getCustomerName (id: number | string): string
{
    return Store.getState().customer.byId.get(id)?.name ?? id.toString();
}


/**
 * I get all customers that have a contact.
 *
 * :param channel: contact channel type id
 * :param uid: contact uid
 *
 * :returns: all customers found
 */
function getCustomersByContact (channel: number, uid: string): ICustomer[]
{
    // create array to store result
    const result: ICustomer[] = [];

    // find customers which contains passed contact
    for (const customer of getState().byId.values())
    {
        // find contact
        for (const item of customer.contacts)
        {
            // customer found: add it to result and proceed to next customer
            if (item.value === uid && item.type_id === channel)
            {
                result.push(customer);
                continue;
            }
        }
    }

    // return all customers found
    return result;
}


/**
 * I get the search term.
 *
 * :returns: search term
 */
function getSearchTerm(): string
{
    return getState().searchTerm;
}


/**
 * I get the siblings of a contact on screen.
 *
 * :param id: contact id
 *
 * :returns: uids the contact is also known by, null while they are not loaded
 */
function getSiblings (id: IContact['id']): string[] | null
{
    return getState().siblingsByContact.get(id) ?? null;
}


/**
 * I get customers aggregate state.
 *
 * :returns: customers aggregate state
 */
function getState(): ICustomerState
{
    return Store.getState().customer;
}


/**
 * I get customers by tags.
 *
 * :param tagsFilter: tags that will filter customers for
 * :param customers: customers to be filtered
 *
 * :returns: found customers
 */
function searchByTags (
    tagsFilter: number[],
    customers: ISearch[] = [],
): ISearch[]
{
    // get customers from params if exists or from store if not
    const reducedCustomers = customers.length > 0 ?
        customers : [...getState().byId.values()];

    // set filtered customers
    const filtered: ISearch[] = [];

    // map through each customer
    for (const customer of reducedCustomers)
    {
        // get customer tags
        const tags = Object.values(customer.tags).map((tag) => tag.tag);

        // no tag association: jump customer
        if (tags.length === 0)
        {
            continue;
        }

        // customer has all tags from filter: push it
        if (tagsFilter.every((tag) => tags.includes(tag)))
        {
            filtered.push(customer);
        }
    }

    // return founded customers
    return filtered;
}


/**
 * I get customers by search term.
 *
 * :param searchTerm: search term
 * :param unassociated: whether it should or not return unassociated customers
 *
 * :returns: found customers
 */
function searchCustomers (searchTerm: string, unassociated = true): ISearch[]
{
    // remove diacritics from search term
    const term = removeDiacritics(searchTerm);

    // initialize found customers
    const found: ISearch[] = [];

    // turn customers into an array
    let customers: ICustomerById = getState().byId;

    // should not return unassociated customers: discard them
    if (unassociated === false)
    {
        customers = customers.filter(
            customer => customer.unassociated === false,
        );
    }

    // customer data to be matched on search
    const searchBy: string[] = [
        'name',
        'document',
        'organization',
        'code',
    ];

    // get customers by search term
    for (const customer of customers.values())
    {
        // index default value
        let index: number | null = null;

        // initialize matched key
        let foundBy: string | null = null;

        // search on each customer property
        for (const key of searchBy)
        {
            // search term on key value
            index = search(customer[key], term);

            // match found: save matched key and stop searching this customer
            if (index !== null)
            {
                foundBy = key;
                break;
            }
        }

        // no matches yet: search term in customer contacts
        if (index === null)
        {
            // search customer by its contacts
            for (const contact of customer.contacts)
            {
                // search term on contact value
                index = search(contact.value, term);

                // no match: search term on the label the contact is shown by
                if (index === null)
                {
                    index = search(contact.displayName, term);
                }

                // match found: save matched contact and stop searching this customer
                if (index !== null)
                {
                    foundBy = 'contact';
                    break;
                }
            }
        }

        // customer matched: add it to the list
        if (index !== null)
        {
            // initialize name highlight
            let nameHighlighted = `<mark></mark>${customer.name}`;

            // customer matched by name: highlight it
            if (foundBy === 'name')
            {
                nameHighlighted = highlight(customer.name, searchTerm, index);
            }

            // add customer to the list
            found.push({...customer, nameHighlighted});
        }
    }

    // return found customers
    return found;
}


/**
 * I get customers by searching their data.
 *
 * :param where: customer data to be matched
 * :param term: user's sanitized input
 *
 * :returns: null or the match
 */
function search(where: string, term: string)
{
    // where is undefined or null: return null
    if (where === undefined || where === null)
    {
        return null;
    }

    // clean where
    const cleanWhere = removeDiacritics(where);

    // get customer name index of search term
    const index = cleanWhere.indexOf(term);

    // return index if greater than -1 or null
    return index > -1 ? index : null;
}


/**
 * I search unassociated customer by given term
 *
 * :param customers: customers list
 * :param term: term to search
 *
 * :returns: searched customers list
 */
function searchUnassociatedCustomers (
    customers: ICustomerById,
    term: string,
): ICustomer[]
{
    // remove diacritics from search term
    const searchTerm = removeDiacritics(term);

    // search customers by term
    const result = customers.filter((customer) =>
    {
        // customer is associated: ignore it
        if (customer.unassociated !== true)
        {
            return false;
        }

        // format customer name
        const name = removeDiacritics(customer.name);

        // name includes search term: add to result
        if (name.includes(searchTerm) === true)
        {
            return true;
        }

        // name not match: search on contacts name and display name
        return customer.contacts.some(contact =>
            contact.name?.includes(term) === true ||
            contact.displayName?.includes(searchTerm) === true);
    });

    // return searched customers as array
    return result.valueSeq().toArray();
}


/**
 * EXPORTS
 */
export default {
    fetch,
    getContactByChannelAndUid,
    getContactById,
    getContactInCustomer,
    getCurrentCustomer,
    getCustomerByContact,
    getCustomerName,
    getCustomersByContact,
    getSearchTerm,
    getSiblings,
    getState,
    searchByTags,
    searchCustomers,
    searchUnassociatedCustomers,
};
